/**
 * Crypto Clicker - Entry Point
 * Functional architecture: core + view modules
 */
import './styles.css';
import { createGameAPI } from '@app-games/api';
import { createGameCore, type GameState, type GameAction } from './game/index.js';
import { GENERATORS } from './game/config/generators.js';
import { ACHIEVEMENTS } from './game/config/achievements.js';
import { GOLDEN_EVENTS } from './game/config/goldenEvents.js';
import { getCurrentCPS, getCurrentClickPower, createInitialMarketState, createInitialDarkPoolState, migrateExchangeState, migrateInfoState, migrateWhales, migrateRiskState, migrateLeadRoomState, migrateLeaderboardState, migrateOnboarding } from './game/state.js';
import { beginRugPull } from './game/systems/rugPullSystem.js';
import { delistCoin, listCoin, listableCoinIds } from './game/systems/listingSystem.js';
import { DARKPOOL_CONFIG } from './game/config/darkpool.js';
import { formatNumber, formatTime, calculateOfflineEarnings } from './game/utils.js';
import { t } from './i18n/index.js';
import { initSound, playSfx } from './sound.js';
import { initSafeArea } from './utils/safeArea.js';
import { initRenderer, buildLayout, dispatch, getState, getCore, showToast, showModal, requestRender, markScreenDirty, setViewState, getViewState } from './view/renderer.js';
import { createPixiManager, type PixiManager } from './view/pixi.js';
import { initMainScreen } from './view/mainScreen.js';
import { initExchangeScreen } from './view/exchangeScreen.js';
import { initCoinDetailScreen } from './view/coinDetailScreen.js';
import { initLeadRoomScreen } from './view/leadRoomScreen.js';
import { initLeaderboardScreen } from './view/leaderboardScreen.js';
import { initUpgradeScreen } from './view/upgradeScreen.js';
import { initPrestigeScreen } from './view/prestigeScreen.js';
import { initAchievementScreen } from './view/achievementScreen.js';
import { initSettingsScreen } from './view/settingsScreen.js';
import { initDarkPoolScreen } from './view/darkPoolScreen.js';
import { checkRiskToasts } from './view/riskHooksPanel.js';
import { initOnboarding } from './view/onboardingController.js';
import { AdManager, getAdRewards } from './monetization/AdManager.js';

// Constants
const SAVE_KEY = 'crypto_clicker_save';
const SAVE_INTERVAL = 5000;

let pixi: PixiManager;
let lastTickTime = 0;
let loaded = false;
let lastAchCount = 0;
let lastMilestones: Record<string, number> = {};
let lastMilestoneCheck = 0;
async function init() {
  const isDev = import.meta.env.DEV;
  const isDemoMode = new URLSearchParams(window.location.search).get('demo') === '1';

  // Init safe area CSS variables
  initSafeArea();

  // Create headless game core
  const core = createGameCore(isDev);
  createGameAPI(core, { exposeGlobal: isDev || isDemoMode, debug: isDev });

  // Dev-only debug helper: force a rug pull on a meme coin
  if (isDev || isDemoMode) {
    (window as unknown as Record<string, unknown>).__debugRugPull = (coinId: string) => {
      core.reset(beginRugPull(core.getState(), coinId, Date.now()));
    };
    // Dev-only: force delist a listed coin (liquidates holding + delist news).
    (window as unknown as Record<string, unknown>).__debugDelist = (coinId: string) => {
      core.reset(delistCoin(core.getState(), coinId, Date.now()));
    };
    // Dev-only: list/revive the given coin (or the first listable one) immediately.
    (window as unknown as Record<string, unknown>).__debugList = (coinId?: string) => {
      const s = core.getState();
      const id = coinId ?? listableCoinIds(s, Date.now())[0];
      if (id) core.reset(listCoin(s, id, Date.now()));
    };
    // Dev-only: fast-forward the info economy + lead rooms so a news item and
    // every room's first call publish immediately (verification / screenshots).
    (window as unknown as Record<string, unknown>).__debugSpeedInfo = () => {
      const s = core.getState();
      const now = Date.now();
      const info = { ...s.info, nextLeakAt: now - 1, nextPublishAt: now - 1 };
      const rooms = Object.fromEntries(
        Object.entries(s.leadRooms.rooms).map(([id, r]) => [id, { ...r, nextCallAt: now - 1 }])
      );
      core.reset({ ...s, info, leadRooms: { ...s.leadRooms, rooms } });
    };
  }

  // Init renderer with core reference
  initRenderer(core);

  // Load saved game (skip in demo mode to start fresh)
  if (!isDemoMode) {
    loadGame(core);
  }

  // Build DOM layout (HUD + tabs + toast)
  buildLayout();

  // Init PixiJS
  pixi = createPixiManager((x, y) => onTap(x, y));
  const pixiContainer = document.getElementById('pixi-container');
  if (pixiContainer) {
    try {
      await pixi.init(pixiContainer);
    } catch (e) {
      console.warn('[PixiJS] init failed, running without visual effects:', e);
    }
  }

  // DOM touch overlay for coin (fallback for WebViews where PixiJS events may fail)
  setupCoinTouchOverlay();

  // Init screen modules (each registers its own onRender callback)
  initMainScreen();
  initExchangeScreen();
  initCoinDetailScreen();
  initLeadRoomScreen();
  initLeaderboardScreen();
  initUpgradeScreen();
  initPrestigeScreen();
  initDarkPoolScreen();
  initAchievementScreen();
  initSettingsScreen();

  // Init ad provider
  AdManager.initialize('auto', {
    admobAndroidRewardedAdUnitId: 'ca-app-pub-5244313691595903/5218272589',
  }).catch(() => {});

  // Init sound system
  initSound();

  // Check offline bonus (skip in demo mode)
  if (!isDemoMode) {
    checkOffline();
  }

  // Initialize milestone tracking from loaded state
  const loadedSt = getState();
  for (const gc of GENERATORS) {
    const gen = loadedSt.generators[gc.id];
    if (gen) lastMilestones[gc.id] = gen.count;
  }

  // Scripted first-session onboarding for new players (skipped in demo mode)
  if (!isDemoMode) initOnboarding();

  // Start game loop
  lastTickTime = performance.now();
  loop();

  // Auto-save (skip in demo mode)
  if (!isDemoMode) {
    setInterval(save, SAVE_INTERVAL);
  }

  // Event listeners
  window.addEventListener('resize', () => {
    pixi.resize();
    positionCoinOverlay();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) save();
  });

  // Demo mode: start DemoPlayer
  if (isDemoMode) {
    // Initial seed generator for fastForward to produce coins
    core.dispatch({ type: 'BUY_GENERATOR', payload: { generatorId: 'gen1' } } as GameAction);

    import('./demo/DemoPlayer.js').then(({ DemoPlayer }) => {
      const demo = new DemoPlayer();
      demo.start();
      (window as unknown as Record<string, unknown>).__demo = demo;
    });
  }

  // Initial render
  setViewState({ currentTab: 'main' });
}

// ===== Coin Touch Overlay (DOM fallback) =====
let coinOverlay: HTMLElement | null = null;

function setupCoinTouchOverlay() {
  coinOverlay = document.createElement('div');
  coinOverlay.id = 'coin-touch-overlay';
  coinOverlay.style.cssText = 'position:fixed;border-radius:50%;z-index:5;pointer-events:auto;touch-action:none;';
  document.body.appendChild(coinOverlay);
  positionCoinOverlay();

  const handleTap = (e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onTap(e.clientX, e.clientY);
  };
  coinOverlay.addEventListener('pointerdown', handleTap);
}

function positionCoinOverlay() {
  if (!coinOverlay || !pixi) return;
  const { x, y, r } = pixi.getCoinCenter();
  const size = r * 2;
  coinOverlay.style.left = `${x - r}px`;
  coinOverlay.style.top = `${y - r}px`;
  coinOverlay.style.width = `${size}px`;
  coinOverlay.style.height = `${size}px`;
}

function onTap(x: number, y: number) {
  const st = getState();
  if (!st.isRunning) return;
  const power = getCurrentClickPower(st);
  dispatch({ type: 'CLICK' });
  dispatch({ type: 'DOGE_CLICK' });
  pixi.bounceCoin();
  const combo = getState().combo.count;
  pixi.spawnParticle(x, y, power, false, combo);
  pixi.showComboText(combo);
  playSfx('tap');
}

function loop() {
  requestAnimationFrame(loop);

  const now = performance.now();
  const dt = now - lastTickTime;
  lastTickTime = now;

  // Tick headless core (runs all registered systems)
  getCore().tick(dt);

  const st = getState();

  // Update golden event button
  updateGoldenButton(st);

  // Check achievement toasts
  checkAchToast(st);

  // Check milestone toasts (throttled to ~1s)
  if (now - lastMilestoneCheck > 1000) {
    checkMilestones(st);
    lastMilestoneCheck = now;
  }

  // Update coin glow based on CPS
  pixi.updateGlow(getCurrentCPS(st));

  // Dark pool countdown banner
  updateDarkPoolBanner(st);

  // Insurance payout toast (rug pull while insured)
  checkRiskToasts(st);

  // Request render (HUD + active tab callbacks)
  requestRender();
}

// ===== Golden Event Button =====
function updateGoldenButton(st: GameState) {
  const existing = document.getElementById('golden-btn');
  if (st.goldenEvent.active) {
    if (!existing) {
      const btn = document.createElement('div');
      btn.id = 'golden-btn';
      btn.innerHTML = '<img src="images/golden-coin.png" alt="" style="width:100%;height:100%;object-fit:contain;pointer-events:none" />';
      btn.addEventListener('click', () => claimGolden());
      document.body.appendChild(btn);
    }
  } else {
    existing?.remove();
  }
}

function claimGolden() {
  const st = getState();
  if (!st.goldenEvent.active) return;
  const ec = GOLDEN_EVENTS.find(e => e.id === st.goldenEvent.type);
  const name = ec ? (t('golden_' + ec.id) || ec.name) : '';
  const desc = ec ? (t('golden_' + ec.id + '_desc') || ec.description) : '';

  showModal(
    name || t('golden_event'),
    `<div class="modal-detail">${desc}</div>`,
    [
      {
        label: t('golden_claim_normal'),
        color: 'var(--card-border)',
        onClick: () => {
          dispatch({ type: 'CLAIM_GOLDEN_EVENT', payload: { watchedAd: false } });
          playSfx('golden');
          if (ec) showEffectToast(name, desc);
          markScreenDirty();
          requestRender();
        },
      },
      {
        label: t('golden_claim_ad'),
        color: 'var(--gold)',
        onClick: async () => {
          const rewards = getAdRewards();
          const result = await AdManager.showRewardedAd(rewards.GOLDEN_DOUBLE);
          dispatch({ type: 'CLAIM_GOLDEN_EVENT', payload: { watchedAd: !!result } });
          playSfx('golden');
          if (ec) showEffectToast(name, desc + (result ? ' (x2!)' : ''));
          markScreenDirty();
          requestRender();
        },
      },
    ]
  );
}

function showEffectToast(name: string, desc: string) {
  const el = document.createElement('div');
  el.style.cssText = `position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:var(--panel-bg);border:2px solid var(--gold);border-radius:16px;padding:20px 28px;z-index:40;text-align:center;box-shadow:0 0 40px rgba(255,215,0,0.4);animation:fadeUp 2s forwards;`;
  el.innerHTML = `<div style="font-size:20px;font-weight:800;color:var(--gold);margin-bottom:4px">${name}</div><div style="font-size:14px;color:var(--muted)">${desc}</div>`;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 2000);
}

// ===== Dark Pool Countdown Banner =====
let dpBannerEl: HTMLElement | null = null;

function updateDarkPoolBanner(st: GameState) {
  const dp = st.darkPool;
  if (!dp) { dpBannerEl?.remove(); dpBannerEl = null; return; }

  const tab = getViewState().currentTab;
  const now = Date.now();

  // Show banner when: idle phase, not on darkpool tab, countdown <= 30s
  const shouldShow = dp.phase === 'idle' && tab !== 'darkpool' && dp.nextRoundTime > now;
  // Also show when betting is active (to let them know they can join)
  const bettingActive = dp.phase === 'betting' && tab !== 'darkpool';

  if (!shouldShow && !bettingActive) {
    dpBannerEl?.remove();
    dpBannerEl = null;
    return;
  }

  if (!dpBannerEl) {
    dpBannerEl = document.createElement('div');
    dpBannerEl.id = 'dp-countdown-banner';
    dpBannerEl.addEventListener('click', () => {
      setViewState({ currentTab: 'darkpool' });
      markScreenDirty();
      requestRender();
    });
    document.body.appendChild(dpBannerEl);
  }

  if (bettingActive) {
    const rem = Math.max(0, Math.ceil((dp.phaseEndTime - now) / 1000));
    dpBannerEl.textContent = `${t('dp_banner_live') || 'Dark Pool LIVE'} — ${rem}s`;
    dpBannerEl.className = 'dp-banner dp-banner-live';
  } else {
    const rem = Math.max(0, Math.ceil((dp.nextRoundTime - now) / 1000));
    dpBannerEl.textContent = `${t('dp_banner_soon') || 'Dark Pool'} ${rem}s`;
    dpBannerEl.className = 'dp-banner dp-banner-idle';
  }
}

// ===== Achievement Toast =====
function checkAchToast(st: GameState) {
  const cnt = Object.values(st.achievements).filter(a => a.unlocked).length;
  if (cnt > lastAchCount && lastAchCount > 0) {
    for (const ac of ACHIEVEMENTS) {
      const a = st.achievements[ac.id];
      if (a?.unlocked && a.claimedAt && Date.now() - a.claimedAt < 2000) {
        const achName = t('ach_' + ac.id) || ac.name;
        showToast(`${achName} ${t('ach_unlocked')}`);
        playSfx('achievement');
        break;
      }
    }
  }
  lastAchCount = cnt;
}

// ===== Milestone Toast =====
function checkMilestones(st: GameState) {
  const milestones = [25, 50, 75, 100, 125, 150, 175, 200];
  for (const gc of GENERATORS) {
    const gen = st.generators[gc.id];
    if (!gen) continue;
    const prevCount = lastMilestones[gc.id] || 0;
    const prevMilestone = milestones.filter(m => m <= prevCount).pop() || 0;
    const currMilestone = milestones.filter(m => m <= gen.count).pop() || 0;

    if (currMilestone > prevMilestone && prevCount > 0) {
      const name = t('gen_' + gc.id) || gc.name;
      showToast(`${name} x${currMilestone}!`);
      playSfx('achievement');
    }
    lastMilestones[gc.id] = gen.count;
  }
}

// ===== Offline Bonus =====
function checkOffline() {
  const st = getState();
  if (!st.lastSaveTimestamp) return;
  const elapsed = (Date.now() - st.lastSaveTimestamp) / 1000;
  if (elapsed < 60) return;
  const cps = getCurrentCPS(st);
  if (cps <= 0) return;
  const maxOff = st.prestige2.purchasedUpgrades.includes('meta_offline_time') ? 57600 : 28800;
  // up_global_offline: 50% → 75% (+0.25)
  // buff_offline: 레벨당 +5%
  let offlineRate = 0.5;
  if (st.purchasedUpgrades.includes('up_global_offline')) offlineRate += 0.25;
  const offlineBuffLevel = st.prestige.buffs['buff_offline'] || 0;
  offlineRate += offlineBuffLevel * 0.05;
  const res = calculateOfflineEarnings(cps, elapsed, maxOff, Math.min(offlineRate, 1.0));
  if (res.earnings <= 0) return;

  // Set pending
  (st as any).offlineBonus = { pending: true, earnings: res.earnings, duration: res.duration, capped: res.capped };

  const ov = document.createElement('div');
  ov.className = 'offline-overlay';
  const p = document.createElement('div');
  p.className = 'offline-panel';
  p.innerHTML = `
    <div class="offline-title">${t('offline_title')}</div>
    <div class="offline-time">${formatTime(res.duration)} ${t('offline_duration')}${res.capped ? ` (${t('offline_capped')})` : ''}</div>
    <div class="offline-amount">+${formatNumber(res.earnings)}</div>
  `;
  const btns = document.createElement('div');
  btns.className = 'offline-btns';
  const clm = document.createElement('button');
  clm.className = 'act-btn act-btn-cyan';
  clm.style.flex = '1';
  clm.textContent = t('offline_claim');
  clm.addEventListener('click', () => {
    dispatch({ type: 'CLAIM_OFFLINE_BONUS', payload: { watchedAd: false } });
    ov.remove();
  });
  const adBtn = document.createElement('button');
  adBtn.className = 'act-btn act-btn-gold';
  adBtn.style.flex = '1';
  adBtn.textContent = t('offline_ad_double');
  adBtn.addEventListener('click', async () => {
    const rewards = getAdRewards();
    const result = await AdManager.showRewardedAd(rewards.OFFLINE_DOUBLE);
    dispatch({ type: 'CLAIM_OFFLINE_BONUS', payload: { watchedAd: !!result } });
    ov.remove();
  });
  btns.append(clm, adBtn);
  p.appendChild(btns);
  ov.appendChild(p);
  document.body.appendChild(ov);
}

// ===== Save / Load =====
function save() {
  if (!loaded) return;
  try {
    const st = getState();
    // Strip _outcome to prevent cheating via localStorage inspection
    const saveData = {
      ...st,
      lastSaveTimestamp: Date.now(),
      darkPool: st.darkPool ? { ...st.darkPool, _outcome: null } : st.darkPool,
    };
    localStorage.setItem(SAVE_KEY, JSON.stringify(saveData));
  } catch { /* ignore */ }
}

function loadGame(core: ReturnType<typeof createGameCore>) {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) { loaded = true; return; }
    const saved = JSON.parse(raw);

    // Patch missing fields
    if (!saved.generators) saved.generators = {};
    for (const g of GENERATORS) {
      if (!saved.generators[g.id]) saved.generators[g.id] = { id: g.id, count: 0 };
    }
    if (!saved.achievements) saved.achievements = {};
    for (const a of ACHIEVEMENTS) {
      if (!saved.achievements[a.id]) saved.achievements[a.id] = { id: a.id, unlocked: false };
    }
    if (!Array.isArray(saved.purchasedUpgrades)) saved.purchasedUpgrades = [];

    // Market state migration
    if (!saved.market) {
      saved.market = createInitialMarketState();
    }

    // Dark pool state migration
    if (!saved.darkPool) {
      saved.darkPool = createInitialDarkPoolState();
    } else {
      // Remove legacy chips field
      delete (saved.darkPool as any).chips;
      if (saved.darkPool.tutorialSeen === undefined) {
        saved.darkPool.tutorialSeen = false;
      }
      // Reset any in-progress round (outcome is not saved for anti-cheat)
      saved.darkPool._outcome = null;
      if (saved.darkPool.phase !== 'idle') {
        // Return bet coins if was in betting phase
        if (saved.darkPool.playerBet && saved.darkPool.phase === 'betting') {
          saved.coins = (saved.coins || 0) + saved.darkPool.playerBet.amount;
        }
        saved.darkPool.phase = 'idle';
        saved.darkPool.coins = [];
        saved.darkPool.playerBet = null;
        saved.darkPool.insiderInfo = null;
        saved.darkPool.result = null;
        saved.darkPool.phaseEndTime = 0;
        saved.darkPool.nextRoundTime = Date.now() + DARKPOOL_CONFIG.roundCooldown;
      }
    }

    // Ad boost cooldown migration
    if (saved.adBoostCooldownUntil === undefined) {
      saved.adBoostCooldownUntil = 0;
    }

    // Exchange state migration (v2.0 - fills defaults, keeps existing data)
    saved.exchange = migrateExchangeState(saved.exchange);
    if (!saved.adHookStats) saved.adHookStats = {};

    // Sprint 2a migrations: info economy + whale cycles + rescue cooldowns
    saved.info = migrateInfoState(saved.info);
    saved.whales = migrateWhales(saved.whales);
    if (!saved.rescueCooldowns) saved.rescueCooldowns = {};

    // Sprint 2b migration: scenarios + rug pulls + insurance + rewind
    saved.risk = migrateRiskState(saved.risk);

    // Sprint 3a migration: fake lead rooms + info dealer
    saved.leadRooms = migrateLeadRoomState(saved.leadRooms);

    // Asset leaderboard migration (keeps existing epoch/bestRank if present)
    saved.leaderboard = migrateLeaderboardState(saved.leaderboard);

    // Onboarding migration: existing saves (no onboarding field) skip the flow;
    // a save captured mid-onboarding resumes where it left off.
    saved.onboarding = migrateOnboarding(saved.onboarding);

    core.reset(saved);
  } catch (e) {
    console.warn('[Load]', e);
  }
  loaded = true;
}

// Start
init().catch(console.error);
